import React, {
  useMemo,
  useCallback,
  ReactNode,
  useContext,
  useState,
} from 'react';

import { DealsContext } from '../deals-context';

import { Badge } from '@prisjakt/ui/badge';
import { Lazy } from 'src/components/lazy';
import { PropertyValue } from 'src/components/property-value';
import Rating from 'src/components/rating';
import { ShippingLabel } from 'src/components/shipping-label';
import { StockStatus } from 'src/components/stock-status';
import { TooltipPlacement } from 'src/components/tooltip-new';

import { trackProductCollectionProductClick } from '../../../tracking';

import { Product, ProductCollectionPropertyColumn } from 'src/types';
import { PriceDisplayEnum } from 'src/components/price-list/types';
import { CountTranslation } from 'src/components/rating/types';
import { ProductComparisonProps } from '../types';

import { DiffPercentageRow } from '../styles';
import * as S from './styles-row';

const ImagePlaceholder = <S.ImagePlaceholder />;
const RatingTranslationKey = {
  domain: 'product',
  key: 'product-card.n-ratings',
} as CountTranslation;

type Props = {
  product: Product;
  propertyColumns: ProductCollectionPropertyColumn[];
  isIndicatingNewColumn: boolean;
  highlightedName?: ReactNode;
  onClick?: (link: string) => void;
} & ProductComparisonProps;

type PropertyColumItemProps = {
  column: ProductCollectionPropertyColumn;
  isIndicatingNewColumn: boolean;
  isLastColumn: boolean;
  product: Product;
  linkProps:
    | {
        onClick: () => void;
        'aria-label': string;
        'data-test': string;
      }
    | {
        to: string;
        'aria-label': string;
        'data-test': string;
      };
};

const PropertyColumnItem = ({
  column,
  isIndicatingNewColumn,
  isLastColumn,
  product,
  linkProps,
}: PropertyColumItemProps) => {
  const [visible, setVisible] = useState<boolean>(false);
  return (
    <S.PropertyTd
      key={column.id}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      indicateNewColumn={isIndicatingNewColumn && isLastColumn}
      {...linkProps}
    >
      <S.Tooltip
        forcedVisible={visible}
        placement={TooltipPlacement.LEFT}
        title={getProductProperty(product, column.id)?.pretty || ''}
      >
        <S.PropertyContainer>
          <ProductPropertyValue product={product} column={column} />
        </S.PropertyContainer>
      </S.Tooltip>
    </S.PropertyTd>
  );
};

export const ProductRow = ({
  product,
  propertyColumns,
  isSelectingProducts,
  selectedProducts,
  selectProductById,
  deselectProductById,
  isIndicatingNewColumn,
  highlightedName,
  onClick,
}: Props) => {
  const { getDealDiffPercentage } = useContext(DealsContext);
  const dealDiffPercentage = getDealDiffPercentage(product.id);
  const isSelected = selectedProducts?.some(p => p.id === product.id);
  const onSelect = useCallback(() => {
    if (selectProductById) {
      selectProductById(product.id, 'productCard');
    }
  }, [product.id, selectProductById]);
  const onDeselect = useCallback(() => {
    if (deselectProductById) {
      deselectProductById(product.id, 'productCard');
    }
  }, [product.id, deselectProductById]);
  const onToggleSelected = isSelected ? onDeselect : onSelect;
  const onClickHandler = useCallback(() => {
    trackProductCollectionProductClick(product.id);
    onClick?.(product.pathName);
  }, [product, onClick]);
  const linkProps = useMemo(() => {
    const baseLinkProps = {
      'aria-label': product.name,
      'data-test': 'ProductListItem',
    };

    return isSelectingProducts
      ? { ...baseLinkProps, onClick: onToggleSelected }
      : { ...baseLinkProps, onClick: onClickHandler, to: product.pathName };
  }, [product, isSelectingProducts, onToggleSelected, onClickHandler]);

  const getShippingInfo = () => {
    if (product.priceSummary.includeShipping == null) return undefined;

    return (
      product.priceSummary.includeShipping - (product.priceSummary.regular || 0)
    );
  };

  return (
    <S.Tr selected={isSelected}>
      <S.NameTd>
        <S.NameWrapper>
          {isSelectingProducts && (
            <S.CompareCheckbox
              selected={isSelected}
              onSelect={onSelect}
              onDeselect={onDeselect}
              aria-label={product.name}
            />
          )}
          <S.NameLink {...linkProps}>
            <Lazy offset={0} placeholder={ImagePlaceholder}>
              <S.Image
                name={product.name}
                src={(product.media && product.media.first) || ''}
                fallback={product.category?.logo || undefined}
              />
            </Lazy>
            <S.ProductNameTable>
              {highlightedName || product.name}
            </S.ProductNameTable>
          </S.NameLink>
        </S.NameWrapper>
      </S.NameTd>
      <S.Td {...linkProps}>
        <S.PriceDealContainer>
          {product.priceSummary.regular && (
            <S.Price value={product.priceSummary.regular || ''} />
          )}
          {dealDiffPercentage ? (
            <S.CampaignBadgeWrapper>
              <DiffPercentageRow>
                -{Math.round(dealDiffPercentage)}%
              </DiffPercentageRow>
            </S.CampaignBadgeWrapper>
          ) : null}
        </S.PriceDealContainer>
        <ShippingLabel
          exclShipping={product.priceSummary.regular || 0}
          priceDisplay={PriceDisplayEnum.PRICE}
          shippingInfo={getShippingInfo()}
          compact
        />
      </S.Td>
      <S.Td {...linkProps}>
        <StockStatus status={product.stockStatus || undefined} />
      </S.Td>
      <S.Td {...linkProps}>
        <Rating
          rate={
            product.aggregatedRating?.score || product.userReviewSummary.rating
          }
          count={
            product.aggregatedRating?.count || product.userReviewSummary.count
          }
          singleStar
          showRate
          showCounter
          countTranslation={RatingTranslationKey}
        />
      </S.Td>
      <S.Td {...linkProps}>
        {typeof product.popularity?.inCategory === 'number' && (
          <S.Center>
            <Badge
              color="GREY"
              key="categoryRank"
              text={product.popularity?.inCategory}
            />
          </S.Center>
        )}
      </S.Td>
      {propertyColumns.map((column, index) => {
        const isLastColumn = index === propertyColumns.length - 1;
        return (
          <PropertyColumnItem
            key={column.id}
            column={column}
            product={product}
            isIndicatingNewColumn={isIndicatingNewColumn}
            isLastColumn={isLastColumn}
            linkProps={linkProps}
          />
        );
      })}
    </S.Tr>
  );
};

type ProductPropertyValueProps = {
  product: Product;
  column: ProductCollectionPropertyColumn;
};

const ProductPropertyValue = ({
  product,
  column,
}: ProductPropertyValueProps) => {
  const { getDealDiffPercentage } = useContext(DealsContext);
  // Some property columns are not actual properties, but we still treat them as such for the
  // most part. This handles how to render the values for those on a case-by-case basis.
  if (column.__typename === 'ProductCollectionSimulatedPropertyColumn') {
    switch (column.id) {
      case 'brand':
        return <>{product.brand?.name}</>;
      case 'deal_diff_percentage':
        // eslint-disable-next-line no-case-declarations
        const dealDiffPercentage = getDealDiffPercentage(product.id);
        return dealDiffPercentage ? (
          <S.CampaignBadgeWrapper>
            <DiffPercentageRow>
              -{Math.round(dealDiffPercentage)}%
            </DiffPercentageRow>
          </S.CampaignBadgeWrapper>
        ) : null;
      case 'days_since_indexing':
        return <>{product.indexingDate}</>;
      default:
        return null;
    }
  }

  const property = getProductProperty(product, column.id);
  if (!property) {
    return null;
  }

  return <PropertyValue property={property} />;
};

function getProductProperty(product: Product, propertyId: string) {
  const productProperties =
    product.properties?.nodes && Array.isArray(product.properties?.nodes)
      ? product.properties.nodes
      : [];
  return productProperties.find(p => p.id === propertyId);
}
